import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { OptionalFilter, SideFilterType } from './interface';
import './sideFilters.less';
import {
  clearModalSideFilter,
  clearSideFilter,
} from '../../modules/SearchDatePicker/searchDatePickerSlice';
import { FiFilter } from 'react-icons/fi';
import { isEmpty } from 'lodash';
import { Button } from 'antd';
import { useSearchParams } from 'react-router-dom';
import SideFiltersContent from './SideFiltersContent';
import SideFiltersModal from './SideFiltersModal';
import ClearFiltersButton from './components/ClearFiltersButton';

/**
 * Helper function to parse price string and extract numeric value
 * Example: "460.5953498646932 $" -> 460.5953498646932
 */
const parsePriceString = (priceStr: string): number => {
  if (!priceStr) return Infinity;
  const numericPart = priceStr.match(/[\d.]+/);
  return numericPart ? parseFloat(numericPart[0]) : Infinity;
};

/**
 * Type guard to check if filter is a StarsFilter
 */
const isStarsFilter = (
  filter: OptionalFilter
): filter is {
  attr: string;
  type: 'stars';
  options: { id: number; min: string }[];
} => {
  return filter.type === 'stars' && filter.attr === 'hotel_stars';
};

/**
 * Group duplicate hotel_stars options by id and keep minimum price for each group
 */
const deduplicateHotelStars = (filters: OptionalFilter[]): OptionalFilter[] => {
  return filters.map((filter) => {
    if (isStarsFilter(filter)) {
      // Group options by id
      const groupedById = filter.options.reduce(
        (
          acc: Record<number, { id: number; min: string }[]>,
          option: { id: number; min: string }
        ) => {
          if (option.id !== null && option.id !== undefined) {
            if (!acc[option.id]) {
              acc[option.id] = [];
            }
            acc[option.id].push(option);
          }
          return acc;
        },
        {}
      );

      // For each group, find the option with minimum price
      const deduplicatedOptions = Object.keys(groupedById).map((id) => {
        const options = groupedById[Number(id)];
        if (options.length === 1) {
          return options[0];
        }

        // Find option with minimum price
        return options.reduce(
          (
            minOption: { id: number; min: string },
            currentOption: { id: number; min: string }
          ) => {
            const minPrice = parsePriceString(minOption.min);
            const currentPrice = parsePriceString(currentOption.min);
            return currentPrice < minPrice ? currentOption : minOption;
          }
        );
      });

      return {
        ...filter,
        options: deduplicatedOptions.sort((a, b) => a.id - b.id), // Sort by star rating
      };
    }
    return filter;
  });
};

interface DynamicSorting {
  name: string;
  label: string;
}

export enum SortByNames {
  Price = 'price',
  HotelStars = 'hotel_stars',
  DistanceAsc = 'distance_asc',
  DistanceDesc = 'distance_desc',

  PriceAsc = 'price_asc',
  PriceDesc = 'price_desc',
  HotelStarsAsc = 'hotel_stars_asc',
  HotelStarsDesc = 'hotel_stars_desc',

  DateAsc = 'date_asc',
  DateDesc = 'date_desc',
}

interface Props {
  optional_filters?: OptionalFilter[];
  starsGreaterThanRatingFilter?: boolean;
  pricingMaxLimitAndPlusFilter?: number | undefined;
  withModalScopeParams?: boolean;
  handleOnClear?: () => void;
  isCompact?: boolean;
  isLoading?: boolean;
  hasPartiallyLoaded?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  dynamicSorting?: DynamicSorting[];
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  showPricePerPax?: boolean;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
  isFromPackageListView?: boolean;
}

const MOBILE_WIDTH_THRESHOLD = 750;

const SideFilters = ({
  optional_filters,
  starsGreaterThanRatingFilter,
  pricingMaxLimitAndPlusFilter,
  handleOnClear,
  withModalScopeParams,
  isCompact,
  isLoading,
  hasPartiallyLoaded,
  additionalPrice = 0,
  freeCancellationTooltipText,
  dynamicSorting,
  filterEntityType,
  isReturnFlight,
  showPricePerPax,
  localSideFilters,
  setLocalSideFilters,
  isFromPackageListView = false,
}: Props) => {
  // Deduplicate hotel_stars options on the client side
  const processedFilters = useMemo(() => {
    if (!optional_filters) return [];
    return deduplicateHotelStars(optional_filters);
  }, [optional_filters]);

  // const [searchParams, setSearchParams] = useSearchParams();
  // const [sortDirection, setSortDirection] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileWidth, setIsMobileWidth] = useState<boolean>(
    window?.innerWidth < MOBILE_WIDTH_THRESHOLD
  );
  useEffect(() => {
    const updateIsMobileWidth = (e: any) => {
      setIsMobileWidth(e?.target?.innerWidth < MOBILE_WIDTH_THRESHOLD);
    };
    window.addEventListener('resize', updateIsMobileWidth);
    return () => {
      window.removeEventListener('resize', updateIsMobileWidth);
    };
  }, []);

  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );
  const sideFilters = useMemo(
    () => (withModalScopeParams ? modalSideFilters : reduxSideFilters),
    [withModalScopeParams, modalSideFilters, reduxSideFilters]
  );

  const { sideFilters: sideFilters_t, general: general_t } = useSelector(
    (state: RootState) => {
      return state.config.translations;
    }
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (isFromPackageListView) {
      return;
    }
    const currentSearchParams = new URLSearchParams(window.location.search);
    const updatedSearchParams = new URLSearchParams();
    currentSearchParams.forEach((value, key) => {
      updatedSearchParams.append(key, value);
    });

    for (const key in sideFilters) {
      if (key === 't' || key === 'for') {
        continue;
      }
      if (sideFilters[key]?.length > 0) {
        // Because we attach hotel name to the query params
        // in order to ensure that input is properly encoded to
        // avoid issues with special characters like &, = etc.
        if (key === 'hotel') {
          const encodedArr =
            sideFilters[key] &&
            sideFilters[key].map((hotelName: string) =>
              encodeURIComponent(hotelName)
            );
          updatedSearchParams.set(key, encodedArr.join(','));
          continue;
        }

        updatedSearchParams.set(
          key,
          Array.isArray(sideFilters[key])
            ? sideFilters[key].join(',')
            : sideFilters[key]
        );
      } else if (typeof sideFilters[key] === 'number') {
        updatedSearchParams.set(key, sideFilters[key]?.toString() ?? '');
      } else {
        updatedSearchParams.delete(key);
      }
    }
    const queryString = updatedSearchParams?.toString();
    if (queryString.length > 0) {
      window.history.pushState({}, '', `?${queryString}`);
    }
  }, [sideFilters]);

  useEffect(() => {
    return () => {
      if (!isEmpty(sideFilters)) {
        const fn = withModalScopeParams
          ? clearModalSideFilter
          : clearSideFilter;
        dispatch(fn({ filterEntityType, isReturnFlight }));
      }
    };
  }, [withModalScopeParams]);

  const [filtersShown, setFiltersShown] = useState(true);

  const handleModalToggle = () => {
    if (isMobileWidth) {
      setIsModalOpen(!isModalOpen);
    } else {
      setFiltersShown((prev) => !prev);
    }
  };

  if (!optional_filters || isEmpty(optional_filters)) {
    return null;
  }

  return (
    <>
      {/* Mobile only: floating filters trigger + modal */}
      {isMobileWidth && (
        <>
          {/*
            Render the floating button into document.body via a portal so its
            `position: fixed` always resolves against the viewport. Otherwise a
            transformed/overflow ancestor (e.g. a results list or map wrapper)
            becomes its containing block and breaks the fixed positioning.
          */}
          {createPortal(
            <Button
              icon={<FiFilter size={37} />}
              onClick={handleModalToggle}
              className={`btn-mobile-show-filters filter-svg ${
                filtersShown ? 'btn-style-5' : 'btn-style-4'
              }`}
            >
              {general_t.filters}
            </Button>,
            document.body
          )}

          <SideFiltersModal
            optional_filters={processedFilters}
            starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
            pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
            handleOnClear={handleOnClear}
            isLoading={isLoading}
            hasPartiallyLoaded={hasPartiallyLoaded}
            additionalPrice={additionalPrice}
            freeCancellationTooltipText={freeCancellationTooltipText}
            dynamicSorting={dynamicSorting}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            showPricePerPax
          />
        </>
      )}

      {/* Inline filters for non-compact mode */}
      {!isMobileWidth && (
        <div
          className={`side-filters ${
            filtersShown ? 'hide-filter-on-mobile' : ''
          } ${isCompact ? 'compact' : ''}`}
        >
          <ClearFiltersButton
            optional_filters={processedFilters}
            handleOnClear={() => {
              handleOnClear && handleOnClear();
            }}
            withModalScopeParams={withModalScopeParams}
            inactiveMode="hidden"
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />

          <SideFiltersContent
            optional_filters={processedFilters}
            starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
            pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            hasPartiallyLoaded={hasPartiallyLoaded}
            additionalPrice={additionalPrice}
            freeCancellationTooltipText={freeCancellationTooltipText}
            dynamicSorting={dynamicSorting}
            filterEntityType={filterEntityType}
            isReturnFlight={isReturnFlight}
            showPricePerPax
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        </div>
      )}
    </>
  );
};

export default SideFilters;
